import { useContext, useMemo } from "react";
import tinycolor from 'tinycolor2';
import {
    Box,
    Typography,
    FormGroup,
    MenuItem,
    FormControl,
    Select
} from "@mui/material";
import { useSelector} from "react-redux";
import { AppContext } from "../../App";
import { selectChartConfig } from "../../selectors/chart";
import { LineSeriesOptions } from "../chartV2/chart.typings";
import ColorSelector from "../colorSelector/colorSelector";
import { clampLineThickness, LineThickness } from "../colorSelector/lineStyle.types";
import CommonChartSettingsTabs from "./commonChartSettingsTabs";
import { chartSettingsColorRowLabelSx, chartSettingsColorRowSx, chartSettingsFormGroupSx, chartSettingsRowSx, chartSettingsSectionTitleSx } from "./chartSettingsLayout";

type Props = {
    viewKey: string;
};

const AreaSettingsDialogs = ({ viewKey }: Props) => {
    const appContext = useContext(AppContext)
    const chart = appContext.getChart(viewKey)
    const chartConfig = useSelector(selectChartConfig(viewKey))

    const primarySeriesGroupId = useMemo(() => {
        return chart?.primarySeriesGroup?.config?.id
    }, [chart?.primarySeriesGroup?.config?.id])
    
    const priceSettings = useMemo(() => {
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options
            return (series as any)?.source ?? "close"
        }
        return "close"
    }, [chartConfig.series, chartConfig?.seriesGroups, primarySeriesGroupId])

    const lineColor = useMemo(() => {
        let color = '#33D778'
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options
            color = (series as any)?.lineColor ?? '#33D778'
        }
        return tinycolor(color)
    }, [chartConfig.series, chartConfig?.seriesGroups, primarySeriesGroupId])
    
    const topColor = useMemo(() => {
        let color = 'rgba( 46, 220, 135, 0.4)'
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options
            color = (series as any)?.topColor ?? 'rgba( 46, 220, 135, 0.4)'
        }
        return tinycolor(color)
    }, [chartConfig.series, chartConfig?.seriesGroups, primarySeriesGroupId])

    const bottomColor = useMemo(() => {
        let color = 'rgba( 40, 221, 100, 0)'
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options
            color = (series as any)?.bottomColor ?? 'rgba( 40, 221, 100, 0)'
        }
        return tinycolor(color)
    }, [chartConfig.series, chartConfig?.seriesGroups, primarySeriesGroupId])

    const lineThickness = useMemo(() => {
        let width: number | undefined
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? '']
            width = (series?.options as any)?.lineWidth ?? series?.thickness
        }
        return clampLineThickness(width)
    }, [chartConfig.series, chartConfig?.seriesGroups, primarySeriesGroupId])

    const handleColorChangeSeries = (newColor: string, type: 'lineColor' | 'topColor' | 'bottomColor') => {
        if (!chart) return
        const series = chart.primarySeriesGroup?.getPrimarySeries()
        if (!series) return
        if (type === 'lineColor') {
            series.updateOptions({
                lineColor: newColor,
                topColor: tinycolor(newColor).setAlpha(0.4).toRgbString(),
                bottomColor: tinycolor(newColor).setAlpha(0).toRgbString(),
            } as any);
            return
        }
        series.updateOptions({
            [type]: newColor
        });
    };
    
    const handleSourceChange = (newSource: 'high' | 'close' | 'open' | 'low') => {
        if (chart) {
            chart.primarySeriesGroup?.getPrimarySeries()?.updateOptions({
                source: newSource
            } as LineSeriesOptions)
        }
    };

    const handleThicknessChange = (thickness: LineThickness) => {
        if (chart) {
            chart.primarySeriesGroup?.getPrimarySeries()?.updateOptions({
                lineWidth: thickness
            } as LineSeriesOptions)
        }
    };

    const symbolTabContent = (
        <Box>
            <Typography variant="subtitle1" sx={chartSettingsSectionTitleSx}>
                AREA
            </Typography>
            <FormGroup sx={chartSettingsFormGroupSx}>
                <Box sx={chartSettingsRowSx}>
                    <Typography sx={{ width: 100, fontSize: "14px", flexShrink: 0 }}>Price Source</Typography>
                    <FormControl size="small">
                        <Select
                            value={priceSettings}
                            onChange={(event) => handleSourceChange(event.target.value as 'open' | 'high' | 'low' | 'close')}
                            sx={{ fontSize: "13px", height: 32, minWidth: 100 }}
                        >
                            <MenuItem value="open">Open</MenuItem>
                            <MenuItem value="high">High</MenuItem>
                            <MenuItem value="low">Low</MenuItem>
                            <MenuItem value="close">Close</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
                <Box sx={chartSettingsColorRowSx}>
                    <Typography sx={chartSettingsColorRowLabelSx}>Line</Typography>
                    <ColorSelector compact showOnlySwatch color={lineColor.toHexString()} opacity={lineColor.getAlpha()}
                        lineThickness={lineThickness}
                        onLineThicknessChange={handleThicknessChange}
                        onColorChange={(color, transparency) => {
                            const newColor = tinycolor(color).setAlpha(transparency).toHex8String();
                            handleColorChangeSeries(newColor, 'lineColor');
                        }} />
                </Box>
                <Box sx={chartSettingsColorRowSx}>
                    <Typography sx={chartSettingsColorRowLabelSx}>Fill</Typography>
                    <ColorSelector compact showOnlySwatch color={topColor.toHexString()} opacity={topColor.getAlpha()}
                        onColorChange={(color, transparency) => {
                            const newColor = tinycolor(color).setAlpha(transparency).toHex8String();
                            handleColorChangeSeries(newColor, 'topColor');
                        }} />
                    <ColorSelector compact showOnlySwatch color={bottomColor.toHexString()} opacity={bottomColor.getAlpha()}
                        onColorChange={(color, transparency) => {
                            const newColor = tinycolor(color).setAlpha(transparency).toHex8String();
                            handleColorChangeSeries(newColor, 'bottomColor');
                        }} />
                </Box>
            </FormGroup>
        </Box>
    );

    return (
        <CommonChartSettingsTabs
            viewKey={viewKey}
            symbolTabContent={symbolTabContent}
        />
    );
};

export default AreaSettingsDialogs;
